import React, { useState } from "react";
import { MdPictureAsPdf, MdPrint } from "react-icons/md";
import { convertToIndianCurrency } from "../../../../utils/numberToWords";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import logo from "../../../../assets/vetrivikaslogo.jpeg";
import "./ForeclosureReceipt.css";

const DENOMINATION_ORDER = ["500", "200", "100", "50", "20", "10", "Coins"];

const rupees = (value) => `₹${Math.round(Number(value) || 0).toLocaleString("en-IN")}`;

const formatDate = (value) => {
  const date = value ? new Date(value) : null;
  return date && !isNaN(date.getTime())
    ? date.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })
    : "-";
};

const DetailList = ({ rows }) => (
  <dl className="fcr-dl">
    {rows.map(({ label, value, tone }) => (
      <div className={`fcr-dl-row ${tone ? `fcr-dl-${tone}` : ""}`} key={label}>
        <dt>{label}</dt>
        <dd>{value}</dd>
      </div>
    ))}
  </dl>
);

const ReceiptForm = ({
  customerDetails,
  memberData,
  denominations,
  foreCloseCharges,
  foreCloseChargesAmount,
  foreCloseGST,
  totalPayableAmount,
  securityDeposit,
  isSecurityDepositAdjusted,
  netPayableAmount,
  collectedAmount,
  waiverAmount,
}) => {
  const [downloading, setDownloading] = useState(false);

  // The customer's receipt must state what they actually PAID. Before waivers
  // existed this printed totalPayableAmount, which is wrong the moment a
  // settlement is negotiated down. Falls back to the payable when no waiver.
  const paidAmount =
    collectedAmount != null ? Number(collectedAmount) : Number(totalPayableAmount || 0);
  const waivedAmount = Number(waiverAmount || 0);
  const depositRecorded = securityDeposit !== "" && securityDeposit != null;
  const centerName = memberData?.fk_member_details_belongsTo_center_centerId?.name;
  const groupName = memberData?.fk_member_details_belongsTo_group_groupId?.leaderName;

  const customerRows = [
    { label: "Customer Name", value: customerDetails?.memberName || "-" },
    { label: "Loan Account No", value: memberData?.fedLanNo || "-" },
    { label: "Prospect ID", value: memberData?.ApplicationId || "-" },
    { label: "PAN", value: memberData?.panNo || "-" },
    { label: "Branch", value: customerDetails?.branch || "-" },
    ...(centerName ? [{ label: "Center", value: centerName }] : []),
    ...(groupName ? [{ label: "Group", value: groupName }] : []),
  ];

  const settlementRows = [
    { label: "Outstanding Principal", value: rupees(customerDetails?.outstandingPrincipal) },
    { label: `Foreclosure Charges (${Number(foreCloseCharges || 0)}%)`, value: rupees(foreCloseChargesAmount) },
    { label: "GST on Charges (18%)", value: rupees(foreCloseGST) },
    { label: "Total Payable", value: rupees(totalPayableAmount), tone: "total" },
    ...(depositRecorded
      ? [
          {
            label: `Security Deposit (${isSecurityDepositAdjusted ? "returned, deducted" : "not returned"})`,
            value: isSecurityDepositAdjusted ? `- ${rupees(securityDeposit)}` : rupees(securityDeposit),
          },
          { label: "Net Payable", value: rupees(netPayableAmount), tone: "total" },
        ]
      : []),
    ...(waivedAmount > 0 ? [{ label: "Waiver (Discount)", value: rupees(waivedAmount), tone: "waiver" }] : []),
    { label: "Amount Paid", value: rupees(paidAmount) },
  ];

  const cashRows = DENOMINATION_ORDER.map((denom) => {
    const count = Number(denominations?.[denom]) || 0;
    return { denom, count, amount: denom === "Coins" ? count : count * Number(denom) };
  }).filter((row) => row.count > 0);

  const handleDownloadPdf = async (event) => {
    event.preventDefault();
    const receipt = document.querySelector(".fcr-doc");
    if (!receipt) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(receipt, { scale: 2, useCORS: true, logging: false, backgroundColor: "#FFFFFF" });
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const imgWidth = pdf.internal.pageSize.getWidth() - 20;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(canvas.toDataURL("image/jpeg", 1.0), "JPEG", 10, 10, imgWidth, imgHeight);
      pdf.save(`Foreclosure_Receipt_${memberData?.ApplicationId || memberData?.fedLanNo || "document"}.pdf`);
    } finally {
      setDownloading(false);
    }
  };

  const handlePrint = (event) => {
    event.preventDefault();
    document.body.classList.add("fcr-printing");
    const done = () => {
      document.body.classList.remove("fcr-printing");
      window.removeEventListener("afterprint", done);
    };
    window.addEventListener("afterprint", done);
    window.print();
  };

  return (
    <>
      <div className="fcr-actions">
        <button type="button" className="report-btn" onClick={handleDownloadPdf} disabled={downloading}>
          <MdPictureAsPdf /> {downloading ? "Preparing PDF…" : "Download PDF"}
        </button>
        <button type="button" className="report-btn fcr-btn-secondary" onClick={handlePrint}>
          <MdPrint /> Print Receipt
        </button>
      </div>

      <article className="fcr-doc">
        <header className="fcr-head">
          <img src={logo} alt="Vetri Vikas Capital Trust" className="fcr-logo" />
          <div className="fcr-head-meta">
            <div className="fcr-title-row">
              <h2 className="fcr-title">Foreclosure Receipt</h2>
              {memberData?.loanType && <span className="fcr-chip">{memberData.loanType}</span>}
              <span className="fcr-chip fcr-chip-good">Foreclosed</span>
            </div>
            <div className="fcr-sub">
              Vetri Vikas Capital Trust · Received towards closure of the loan account
            </div>
            <div className="fcr-sub">
              Receipt No. <strong>{customerDetails?.approvalId || "-"}</strong>
              {"  ·  "}Approved on <strong>{formatDate(customerDetails?.closedOn)}</strong>
              {"  ·  "}Printed on <strong>{formatDate(new Date())}</strong>
            </div>
          </div>
        </header>

        <div className="fcr-body">
          <section className="fcr-paid" aria-label="Amount paid">
            <span className="fcr-paid-label">Amount Received</span>
            <span className="fcr-paid-value">{rupees(paidAmount)}</span>
            <span className="fcr-paid-words">{convertToIndianCurrency(paidAmount)} Only</span>
          </section>

          <div className="fcr-grid">
            <section className="fcr-card">
              <h3 className="fcr-card-title">Customer Details</h3>
              <DetailList rows={customerRows} />
            </section>
            <section className="fcr-card">
              <h3 className="fcr-card-title">Settlement</h3>
              <DetailList rows={settlementRows} />
            </section>
          </div>

          <section className="fcr-card">
            <h3 className="fcr-card-title">Cash Details</h3>
            <table className="fcr-table">
              <thead>
                <tr>
                  <th>Denomination</th>
                  <th>Count</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {cashRows.length > 0 ? (
                  cashRows.map((row) => (
                    <tr key={row.denom}>
                      <td>{row.denom === "Coins" ? "Coins" : `₹${row.denom}`}</td>
                      <td>{row.count}</td>
                      <td>{rupees(row.amount)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3}>No cash denominations recorded</td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td>Total</td>
                  <td />
                  <td>{rupees(paidAmount)}</td>
                </tr>
              </tfoot>
            </table>
          </section>

          <div className="fcr-signs">
            <div className="fcr-sign">Customer Signature</div>
            <div className="fcr-sign">Branch Manager Signature</div>
          </div>
        </div>

        <footer className="fcr-foot">This receipt confirms the amount received for foreclosure of the loan above.</footer>
      </article>
    </>
  );
};

export default ReceiptForm;
